import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { WebAppShell } from '../../design-system/components/WebAppShell';
import { reviewDesk } from '../../design-system/theme/reviewDesk';
import { useTranslation } from '../../hooks/useTranslation';
import MentorTrainingAPI from '../../services/api/mentorTrainingAPI';
import {
  AttemptResponse,
  COMPETENCY_CODES,
  COMPETENCY_LABELS,
  CompetencyCode,
  DrillDetail,
  DrillLevel,
  InstructorAttemptRow,
  LEVEL_LABELS,
} from '../../types/mentorTraining';

function levelDisplay(level: DrillLevel | null | undefined, lang: 'en' | 'tr'): string {
  if (!level) return '—';
  return LEVEL_LABELS[level]?.[lang] ?? level;
}

function scoreColor(score: number): string {
  if (score >= 80) return reviewDesk.success;
  if (score >= 50) return reviewDesk.amber;
  return '#C44C3F';
}

export default function InstructorDrillScreen() {
  const router = useRouter();
  const { t, language } = useTranslation();
  const lang: 'en' | 'tr' = language === 'tr' ? 'tr' : 'en';
  const params = useLocalSearchParams<{ drillId: string }>();
  const drillId = Number(params.drillId);

  const [drill, setDrill] = useState<DrillDetail | null>(null);
  const [rows, setRows] = useState<InstructorAttemptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [attemptCache, setAttemptCache] = useState<Record<number, AttemptResponse>>({});
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(drillId)) {
      setError('Invalid drill id');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [d, attempts] = await Promise.all([
        MentorTrainingAPI.getInstructorDrill(drillId),
        MentorTrainingAPI.listDrillAttempts(drillId),
      ]);
      setDrill(d);
      setRows(attempts);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [drillId]);

  useEffect(() => {
    load();
  }, [load]);

  const onExpand = useCallback(
    async (row: InstructorAttemptRow) => {
      if (row.status === 'not_started' || row.id === 0) return;
      if (expanded === row.id) {
        setExpanded(null);
        return;
      }
      setExpanded(row.id);
      if (!attemptCache[row.id]) {
        try {
          const detail = await MentorTrainingAPI.getAttemptDetail(drillId, row.id);
          setAttemptCache((prev) => ({ ...prev, [row.id]: detail }));
        } catch (e: any) {
          setError(e?.message || String(e));
        }
      }
    },
    [expanded, attemptCache, drillId]
  );

  const onArchive = useCallback(async () => {
    if (!drill) return;
    setArchiving(true);
    try {
      await MentorTrainingAPI.archiveDrill(drill.id);
      router.push('/mentor-training' as any);
    } catch (e: any) {
      setError(e?.message || String(e));
      setArchiving(false);
    }
  }, [drill, router]);

  const summaryStats = useMemo(() => {
    const submitted = rows.filter((r) => r.status === 'submitted').length;
    const inProgress = rows.filter((r) => r.status === 'in_progress').length;
    const notStarted = rows.length - submitted - inProgress;
    const scores = rows
      .map((r) => r.score_percent)
      .filter((s): s is number => typeof s === 'number');
    const avg = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
    return { submitted, inProgress, notStarted, avg };
  }, [rows]);

  const headerRight = useMemo(() => {
    if (!drill) return null;
    return (
      <View style={styles.topRightRow}>
        <TouchableOpacity
          onPress={() => router.push('/mentor-training' as any)}
          style={styles.backBtn}
        >
          <MaterialIcons name="arrow-back" size={16} color={reviewDesk.inkSoft} />
          <Text style={styles.backBtnText}>
            {t('mentorTraining.back')}
          </Text>
        </TouchableOpacity>
        {!drill.archived_at && (
          <TouchableOpacity
            onPress={onArchive}
            disabled={archiving}
            style={[styles.archiveBtn, archiving && { opacity: 0.5 }]}
          >
            <MaterialIcons name="inventory-2" size={14} color={reviewDesk.inkSoft} />
            <Text style={styles.archiveBtnText}>
              {t('mentorTraining.archive')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }, [drill, router, t, onArchive, archiving]);

  if (loading) {
    return (
      <WebAppShell active="mentor-training">
        <View style={styles.loaderBox}>
          <ActivityIndicator size="large" color={reviewDesk.accent} />
        </View>
      </WebAppShell>
    );
  }

  if (error || !drill) {
    return (
      <WebAppShell active="mentor-training">
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error || 'Drill not found'}</Text>
        </View>
      </WebAppShell>
    );
  }

  const groundTruth = (drill as any).competency_codes ? null : null; // placeholder

  return (
    <WebAppShell active="mentor-training" topRight={headerRight}>
      <View style={styles.container}>
        <View style={styles.summaryCard}>
          <View style={styles.summaryHeader}>
            <View style={styles.summaryLeft}>
              {!!drill.module_name && <Text style={styles.moduleLabel}>{drill.module_name}</Text>}
              <Text style={styles.drillTitle}>{drill.title}</Text>
              <View style={styles.summaryMeta}>
                <View style={styles.metaPill}>
                  <Text style={styles.metaPillText}>{drill.target_level.toUpperCase()}</Text>
                </View>
                <Text style={styles.metaText}>
                  {drill.published_at
                    ? `${t('mentorTraining.publishedOn')}: ${new Date(drill.published_at).toLocaleDateString()}`
                    : t('mentorTraining.notPublished')}
                </Text>
              </View>
            </View>
            <View style={styles.summaryStatsCol}>
              <View style={styles.statBlock}>
                <Text style={styles.statValue}>{summaryStats.submitted}</Text>
                <Text style={styles.statLabel}>{t('mentorTraining.stats.submitted')}</Text>
              </View>
              <View style={styles.statBlock}>
                <Text style={styles.statValue}>{summaryStats.inProgress}</Text>
                <Text style={styles.statLabel}>{t('mentorTraining.stats.inProgress')}</Text>
              </View>
              <View style={styles.statBlock}>
                <Text style={styles.statValue}>{summaryStats.notStarted}</Text>
                <Text style={styles.statLabel}>{t('mentorTraining.stats.notStarted')}</Text>
              </View>
              <View style={[styles.statBlock, styles.statBlockHero]}>
                <Text style={[styles.statValue, { color: reviewDesk.accent }]}>
                  {summaryStats.avg !== null ? `${summaryStats.avg}%` : '—'}
                </Text>
                <Text style={styles.statLabel}>{t('mentorTraining.stats.avgScore')}</Text>
              </View>
            </View>
          </View>
          {!!drill.instructions && (
            <View style={styles.instructionsBox}>
              <MaterialIcons name="lightbulb-outline" size={14} color={reviewDesk.amber} />
              <Text style={styles.instructionsText}>{drill.instructions}</Text>
            </View>
          )}
        </View>

        <View style={styles.tableCard}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableTitle}>
              {t('mentorTraining.submissions')} ({rows.length})
            </Text>
          </View>
          {rows.length === 0 ? (
            <View style={styles.emptyBox}>
              <Text style={styles.emptyText}>
                {t('mentorTraining.noMembersYet')}
              </Text>
            </View>
          ) : (
            <View>
              <View style={styles.tableRowHead}>
                <Text style={[styles.tableCell, styles.cellName]}>{t('mentorTraining.table.student')}</Text>
                <Text style={[styles.tableCell, styles.cellStatus]}>{t('mentorTraining.table.status')}</Text>
                <Text style={[styles.tableCell, styles.cellScore]}>{t('mentorTraining.table.score')}</Text>
                <Text style={[styles.tableCell, styles.cellAlign]}>{t('mentorTraining.table.alignment')}</Text>
                <Text style={[styles.tableCell, styles.cellAction]}> </Text>
              </View>
              {rows.map((row) => {
                const expand = expanded === row.id && row.id !== 0;
                const cached = attemptCache[row.id];
                return (
                  <View key={`${row.student_user_id}-${row.id}`} style={styles.rowGroup}>
                    <Pressable
                      onPress={() => onExpand(row)}
                      style={({ hovered }: any) => [
                        styles.tableRow,
                        hovered && row.id !== 0 && { backgroundColor: reviewDesk.cardMuted },
                        expand && { backgroundColor: reviewDesk.accentWash },
                      ]}
                    >
                      <View style={[styles.tableCellBox, styles.cellName]}>
                        <Text style={styles.studentName}>
                          {row.student_name || row.student_email || '—'}
                        </Text>
                        {!!row.student_email && row.student_name && (
                          <Text style={styles.studentEmail}>{row.student_email}</Text>
                        )}
                      </View>
                      <View style={[styles.tableCellBox, styles.cellStatus]}>
                        <StatusBadge status={row.status} t={t} />
                      </View>
                      <View style={[styles.tableCellBox, styles.cellScore]}>
                        {row.score_percent !== null ? (
                          <Text style={[styles.scoreText, { color: scoreColor(row.score_percent) }]}>
                            {row.score_percent}%
                          </Text>
                        ) : (
                          <Text style={styles.dim}>—</Text>
                        )}
                      </View>
                      <View style={[styles.tableCellBox, styles.cellAlign]}>
                        {row.alignment_score !== null ? (
                          <Text style={[styles.scoreText, { color: scoreColor(row.alignment_score) }]}>
                            {row.alignment_score}
                          </Text>
                        ) : (
                          <Text style={styles.dim}>—</Text>
                        )}
                      </View>
                      <View style={[styles.tableCellBox, styles.cellAction]}>
                        {row.id !== 0 && (
                          <MaterialIcons
                            name={expand ? 'expand-less' : 'expand-more'}
                            size={20}
                            color={reviewDesk.muted}
                          />
                        )}
                      </View>
                    </Pressable>
                    {expand && cached && (
                      <ExpandedAttempt attempt={cached} drill={drill} lang={lang} t={t} />
                    )}
                    {expand && !cached && (
                      <View style={styles.expandedLoader}>
                        <ActivityIndicator size="small" color={reviewDesk.accent} />
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </View>
    </WebAppShell>
  );
}

function StatusBadge({ status, t }: { status: string; t: (k: string, fb?: string) => string }) {
  let label = '—';
  let bg: string = reviewDesk.cardMuted;
  let color: string = reviewDesk.inkSoft;
  if (status === 'submitted') {
    label = t('mentorTraining.status.submitted');
    bg = reviewDesk.successSoft;
    color = reviewDesk.success;
  } else if (status === 'in_progress') {
    label = t('mentorTraining.status.inProgress');
    bg = reviewDesk.amberSoft;
    color = reviewDesk.amber;
  } else if (status === 'not_started') {
    label = t('mentorTraining.status.notStarted');
  }
  return (
    <View style={[styles.badge, { backgroundColor: bg }]}>
      <Text style={[styles.badgeText, { color }]}>{label}</Text>
    </View>
  );
}

function ExpandedAttempt({
  attempt,
  drill,
  lang,
  t,
}: {
  attempt: AttemptResponse;
  drill: DrillDetail;
  lang: 'en' | 'tr';
  t: (k: string, fb?: string) => string;
}) {
  const grading = attempt.student_grading;
  const agreement = attempt.agreement_result;
  const critique = attempt.feedback_critique;

  return (
    <View style={styles.expandedBox}>
      <View style={styles.expandedSection}>
        <Text style={styles.expandedSectionTitle}>
          {t('mentorTraining.studentGrading')}
        </Text>
        {grading && agreement ? (
          <View style={styles.compGrid}>
            {agreement.per_competency.map((row) => (
              <View key={row.code} style={styles.compMini}>
                <Text style={styles.compMiniCode}>{row.code}</Text>
                <Text style={styles.compMiniName}>
                  {COMPETENCY_LABELS[row.code as CompetencyCode]?.[lang] ?? row.code}
                </Text>
                <View style={styles.compMiniLevels}>
                  <Text style={styles.compMiniStudent}>{levelDisplay(row.student_level, lang)}</Text>
                  <Text style={styles.compMiniArrow}> → </Text>
                  <Text style={[styles.compMiniTruth, { color: reviewDesk.accent }]}>
                    {levelDisplay(row.ground_truth_level, lang)}
                  </Text>
                </View>
                <Text style={[styles.compMiniScore, { color: scoreColor(row.score) }]}>
                  {row.score}
                </Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.dim}>{t('mentorTraining.noData')}</Text>
        )}
      </View>

      {grading?.written_feedback && (
        <View style={styles.expandedSection}>
          <Text style={styles.expandedSectionTitle}>
            {t('mentorTraining.studentFeedback')}
          </Text>
          <Text style={styles.feedbackHead}>{t('mentorTraining.strength')}</Text>
          <Text style={styles.feedbackText}>{grading.written_feedback.strength}</Text>
          <Text style={styles.feedbackHead}>{t('mentorTraining.growth')}</Text>
          <Text style={styles.feedbackText}>{grading.written_feedback.growth}</Text>
        </View>
      )}

      {critique && (
        <View style={styles.expandedSection}>
          <Text style={styles.expandedSectionTitle}>
            {t('mentorTraining.results.critiqueTitle')}
            {' · '}
            <Text style={{ color: scoreColor(critique.alignment_score) }}>
              {critique.alignment_score}/100
            </Text>
          </Text>
          <Text style={styles.feedbackHead}>
            {t('mentorTraining.results.strengthCritique')} · {critique.strengths_quality.score}/10
          </Text>
          <Text style={styles.feedbackText}>{critique.strengths_quality.review}</Text>
          <Text style={styles.feedbackHead}>
            {t('mentorTraining.results.growthCritique')} · {critique.growth_quality.score}/10
          </Text>
          <Text style={styles.feedbackText}>{critique.growth_quality.review}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { paddingHorizontal: 24, paddingVertical: 18, gap: 16 },
  topRightRow: { flexDirection: 'row', gap: 10, alignItems: 'center' },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  backBtnText: { fontSize: 13, color: reviewDesk.inkSoft },
  archiveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 6,
    borderColor: reviewDesk.line,
    borderWidth: 1,
  },
  archiveBtnText: { fontSize: 12, color: reviewDesk.inkSoft, fontWeight: '500' },
  loaderBox: { padding: 60, alignItems: 'center' },
  errorBox: { padding: 24, backgroundColor: '#FFE7E1', borderRadius: 10, margin: 20 },
  errorText: { color: '#7A2B1F' },

  summaryCard: {
    padding: 20,
    backgroundColor: reviewDesk.card,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 12,
    gap: 14,
  },
  summaryHeader: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  summaryLeft: { flex: 1, minWidth: 0, flexShrink: 1, gap: 6 },
  summaryStatsCol: { flexDirection: 'row', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' },
  moduleLabel: {
    fontSize: 11,
    color: reviewDesk.muted,
    fontWeight: '600',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  drillTitle: { fontSize: 22, fontWeight: '700', color: reviewDesk.ink },
  summaryMeta: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 },
  metaPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: reviewDesk.accentSoft,
    borderRadius: 5,
  },
  metaPillText: { fontSize: 11, fontWeight: '700', color: reviewDesk.accent, letterSpacing: 0.5 },
  metaText: { fontSize: 12, color: reviewDesk.muted },
  statBlock: { alignItems: 'center', minWidth: 70 },
  statBlockHero: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: reviewDesk.accentSoft,
    borderRadius: 10,
  },
  statValue: { fontSize: 22, fontWeight: '700', color: reviewDesk.ink },
  statLabel: { fontSize: 11, color: reviewDesk.muted, marginTop: 2, textAlign: 'center' },
  instructionsBox: {
    flexDirection: 'row',
    gap: 8,
    padding: 12,
    backgroundColor: reviewDesk.amberSoft,
    borderRadius: 8,
    alignItems: 'flex-start',
  },
  instructionsText: { flex: 1, fontSize: 13, lineHeight: 19, color: reviewDesk.inkSoft },

  tableCard: {
    backgroundColor: reviewDesk.card,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  tableHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomColor: reviewDesk.line,
    borderBottomWidth: 1,
    backgroundColor: reviewDesk.cardMuted,
  },
  tableTitle: { fontSize: 14, fontWeight: '600', color: reviewDesk.ink },
  emptyBox: { padding: 30, alignItems: 'center' },
  emptyText: { color: reviewDesk.muted },
  tableRowHead: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomColor: reviewDesk.line,
    borderBottomWidth: 1,
  },
  tableCell: { fontSize: 11, fontWeight: '600', color: reviewDesk.muted, textTransform: 'uppercase', letterSpacing: 0.4 },
  rowGroup: { borderBottomColor: reviewDesk.line, borderBottomWidth: 1 },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
  },
  tableCellBox: { justifyContent: 'center' },
  cellName: { flex: 3 },
  cellStatus: { flex: 2 },
  cellScore: { flex: 1, alignItems: 'flex-start' },
  cellAlign: { flex: 1, alignItems: 'flex-start' },
  cellAction: { width: 32, alignItems: 'flex-end' },
  studentName: { fontSize: 13, fontWeight: '500', color: reviewDesk.ink },
  studentEmail: { fontSize: 11, color: reviewDesk.muted, marginTop: 2 },
  scoreText: { fontSize: 14, fontWeight: '700' },
  dim: { color: reviewDesk.muted, fontSize: 13 },
  badge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 5, alignSelf: 'flex-start' },
  badgeText: { fontSize: 11, fontWeight: '600' },

  expandedBox: {
    backgroundColor: reviewDesk.cardMuted,
    paddingHorizontal: 18,
    paddingVertical: 18,
    gap: 16,
    borderTopColor: reviewDesk.line,
    borderTopWidth: 1,
  },
  expandedSection: { gap: 8 },
  expandedSectionTitle: { fontSize: 13, fontWeight: '700', color: reviewDesk.ink },
  expandedLoader: { padding: 20, alignItems: 'center' },

  compGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  compMini: {
    width: 220,
    padding: 10,
    backgroundColor: reviewDesk.card,
    borderRadius: 6,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    gap: 4,
  },
  compMiniCode: {
    alignSelf: 'flex-start',
    paddingHorizontal: 6,
    paddingVertical: 1,
    backgroundColor: reviewDesk.accentSoft,
    color: reviewDesk.accent,
    borderRadius: 3,
    fontSize: 10,
    fontWeight: '700',
  },
  compMiniName: { fontSize: 12, fontWeight: '600', color: reviewDesk.ink },
  compMiniLevels: { flexDirection: 'row', alignItems: 'center' },
  compMiniStudent: { fontSize: 12, color: reviewDesk.inkSoft },
  compMiniArrow: { fontSize: 12, color: reviewDesk.muted },
  compMiniTruth: { fontSize: 12, fontWeight: '600' },
  compMiniScore: { fontSize: 14, fontWeight: '700' },

  feedbackHead: { fontSize: 12, fontWeight: '600', color: reviewDesk.inkSoft, marginTop: 6 },
  feedbackText: { fontSize: 13, color: reviewDesk.ink, lineHeight: 19 },
});
